/**
 * MC4 Slice 4 — draft-preview MODEL→PAPER mapping resolver.
 *
 * A draft command that runs INSIDE a paper-space viewport (MSPACE_VIA_VP)
 * authors its preview entity in the VP's MODEL coordinates, but the rubber
 * band is painted on the PAPER camera (the `draftPreviewMesh` group lives in
 * the same scene as the paper sheet). Without a transform those model coords
 * would be read as paper coords and the rubber band would land at the wrong
 * place / scale, OUTSIDE the VP rect.
 *
 * This module derives the `DraftPreviewMapping` (per-vertex `modelToPaper` +
 * the isotropic radius scale) the `DraftPreviewLayer` applies. It reuses the
 * SAME active-VP source the S2/S3 pointer paths consume — the live
 * `ViewportRedirectContext` from `Scene3DStage` (`getActiveViewport()`), and
 * the SAME paper origin (`paperPageSetup.origin`). It does NOT re-resolve the
 * active VP or recompute the paper origin: it consumes the already-resolved
 * values, keeping a single source of truth (CLAUDE.md PRIORITY #2, no
 * duplicated redirect logic).
 *
 * THREE-free by design (same contract as `viewportTransform2d` /
 * `activeInputSpace`): it imports only the planar affine, the coordinate-origin
 * leaf (whose own only import is a TYPE-ONLY `Vec3`) and the structural
 * redirect view, so it never pulls WebGL into a headless path.
 *
 * The gate matches `resolveActiveInputSpace` exactly: map ONLY when
 * `mode === "MSPACE_VIA_VP"`, `activeViewportId != null`, the VP resolves and
 * its id matches, the VP is UNLOCKED, and `height > 0 && viewHeight > 0`.
 * (The cursor-inside-rect test from `resolveActiveInputSpace` is NOT part of
 * this gate: the rubber band must stay mapped while the cursor is mid-draft
 * even as it grazes the VP edge — the input-space router already guarantees
 * the committed points are model-space; here we only need to render whatever
 * model-space preview the session produced back onto paper.)
 */

import { unapplyOffsetToPoint2d } from "../coordinateOrigin";
import {
  modelToPaper,
  viewportPaperRect,
  type PaperOrigin,
} from "../viewports/viewportTransform2d";
import type { ViewportRedirectContextLike } from "../viewports/activeInputSpace";
import type { DraftPreviewMapping } from "./draftPreviewLayer";

/**
 * Resolve the MODEL→PAPER draft-preview mapping for the current frame, or
 * `null` when the preview must NOT be transformed (the common case: plain
 * model / paper drafting, a locked VP, no active VP, or no in-VP preview).
 *
 * @param redirect       the live viewport-redirect context (same ref the
 *                       pointer pick/snap/draft paths read); `null` disables.
 * @param paperOrigin    the active layout's paper origin (`paperPageSetup`),
 *                       the SAME value the redirect context carries; `null`
 *                       disables (no layout page-setup → no paper transform).
 * @param previewSpaceId the draft preview entity's space id. The mapping is
 *                       MODEL→PAPER, so it only applies to a model-space
 *                       preview (`"model"`). `null`/undefined → no preview →
 *                       no mapping.
 */
export function resolveDraftPreviewMapping(
  redirect: ViewportRedirectContextLike | null,
  paperOrigin: PaperOrigin | null,
  previewSpaceId: string | null | undefined,
): DraftPreviewMapping | null {
  if (!redirect || !paperOrigin) return null;
  // Only a MODEL-space preview is mapped MODEL→PAPER. In MSPACE_VIA_VP the
  // draft session is pinned to "model" (useDraftSession.beginDrafting), so the
  // preview entity carries spaceId "model"; any other space means we are not
  // drafting inside a VP and must leave the preview untransformed.
  if (previewSpaceId !== "model") return null;

  // ── Gate (byte-identical to resolveActiveInputSpace, minus the cursor
  //    over-rect test — see module header) ──────────────────────────────────
  if (redirect.mode !== "MSPACE_VIA_VP" || redirect.activeViewportId == null) {
    return null;
  }
  const vp = redirect.getActiveViewport();
  if (!vp || vp.id !== redirect.activeViewportId) return null;
  // Locked VP → input acts on the paper, not the model view; no MODEL→PAPER
  // draft (a locked VP can't be drafted into in the first place).
  if (vp.effectiveLocked) return null;
  if (vp.height <= 0 || vp.viewHeight <= 0) return null;

  // ── Mapped: per-vertex modelToPaper + isotropic radius scale ──────────────
  // The VP transform is rotation (viewTwistAngle) + uniform scale
  // (height / viewHeight), so a circle stays a circle: radius scales by the
  // same isotropic factor (matches the S2/S3 `scale = vp.height/vp.viewHeight`).
  const radiusScale = vp.height / vp.viewHeight;
  // MC4 clip slice — the VP's axis-aligned paper rect (paper coords). The
  // DraftPreviewLayer turns this into 4 world-space THREE.Plane clip planes so
  // the rubber band is clipped to the VP boundary (AutoCAD parity: a draft band
  // never extends past the viewport edge onto the sheet). Reuses the SAME
  // `viewportPaperRect` derivation `pointInViewport` uses — no second rect
  // formula — and the SAME `vp` + `paperOrigin` already resolved by this gate.
  // Twist does NOT rotate this rect (the frame on the sheet is always
  // axis-aligned; `viewTwistAngle` only rotates the model view inside it).
  // Kept as plain numbers (THREE-free module); the Plane objects are built in
  // draftPreviewLayer.
  const clipRect = viewportPaperRect(vp, paperOrigin);
  return {
    // DRAFT-DOCUMENT-FRAME — `mapPoint` is applied by
    // `draftPreviewLayer.renderableToDraftUpdate`, i.e. AFTER
    // `entityToRenderable` has already run every vertex through
    // `applyOffset` (see `adapters/sceneGeometryBuilders.ts`). So the vertex
    // arriving here is OFFSET-world, while `modelToPaper` subtracts the VP's
    // WCS view centre (`vp.viewTarget + vp.viewCenter`), a DOCUMENT-frame
    // model coordinate (`viewportTransform2d`'s `PaperOrigin` doc). The
    // target reaches `vp` from `Scene3DStage`'s `getActiveViewport`, the same
    // resolved VP the pointer paths consume, so this mapping and the on-screen
    // per-VP camera stay on ONE anchor. Mixing the frames leaves the
    // whole session origin in the mapped result — measured as a rubber band
    // a million units off the sheet on any drawing with a non-zero origin.
    //
    // Un-offset first, do the affine entirely in the document frame, and the
    // OFFSET-world `paperOrigin` this function was handed
    // (`Scene3DStage.paperOriginOffset`) puts the output straight back into
    // the render frame the sheet, the VP frame meshes and `clipRect` are all
    // drawn in — one conversion each way, no leftover origin.
    mapPoint: (p) => modelToPaper(unapplyOffsetToPoint2d(p), vp, paperOrigin),
    radiusScale,
    clipRect,
  };
}
